import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useHeaderSearch } from "../lib/headerSearch";
import { useQuery } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { Timeline, type TimelineSort } from "../components/Timeline";
import { NewPostForm } from "../components/NewPostForm";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { apiFetch } from "../lib/api";
import { InstallAppCard } from "../components/InstallAppCard";
import { can, personDisplayName } from "@family/shared";
import type {
  PostsListResponse,
  PersonsListResponse,
  RelationshipsListResponse,
  EventsListResponse,
  TreeStatsResponse,
} from "@family/shared";

/**
 * Pull the first standalone 4-digit year out of a search string and
 * return it together with the remaining "text" portion (year token
 * removed). Plausibility-check the year against the dataset so a stray
 * "10000" doesn't accidentally trigger filtering.
 *
 * "wedding 1979"  → { year: 1979, text: "wedding" }
 * "1979"          → { year: 1979, text: "" }
 * "address 90210" → { year: null,  text: "address 90210" } (zip-code style)
 *                                                            no, 90210
 *                                                            isn't valid
 *                                                            anyway via
 *                                                            min-year
 *                                                            check
 *
 * We accept years 1800–2100 — broader than any realistic family album,
 * but narrow enough to ignore most random 4-digit numbers.
 */
function parseYearFromQuery(raw: string): { year: number | null; text: string } {
  const match = raw.match(/(?:^|\s)(\d{4})(?:\s|$)/);
  if (!match) return { year: null, text: raw.trim() };
  const candidate = Number(match[1]);
  if (candidate < 1800 || candidate > 2100) {
    return { year: null, text: raw.trim() };
  }
  // Strip the matched year token and collapse whitespace.
  const text = raw
    .replace(match[0], " ")
    .replace(/\s+/g, " ")
    .trim();
  return { year: candidate, text };
}

function PostSearch({
  familySlug,
  textQuery,
  yearFilter,
}: {
  familySlug: string;
  textQuery: string;
  yearFilter: number | null;
}) {
  // Backend search endpoint only knows about text. When a year is also
  // supplied, we filter the returned list client-side. Search payloads
  // are small (≤ 20), so this is essentially free.
  const searchQuery = useQuery<PostsListResponse>({
    queryKey: ["posts-search", familySlug, textQuery],
    queryFn: () =>
      apiFetch<PostsListResponse>(
        `/families/${familySlug}/posts/search?q=${encodeURIComponent(textQuery)}`,
      ),
    enabled: textQuery.length >= 2,
  });

  // People search reuses the persons list endpoint's ?q= (same tokenized
  // name match as the family AI). Name-based, so the year filter doesn't
  // apply to it.
  const personsQuery = useQuery<PersonsListResponse>({
    queryKey: ["persons-search", familySlug, textQuery],
    queryFn: () =>
      apiFetch<PersonsListResponse>(
        `/families/${familySlug}/persons?q=${encodeURIComponent(textQuery)}`,
      ),
    enabled: textQuery.length >= 2,
  });

  const results = useMemo(() => {
    const list = searchQuery.data?.posts ?? [];
    if (yearFilter === null) return list;
    return list.filter((p) => {
      if (!p.eventDate) return false;
      // eventDate is YYYY-MM-DD in storage; just match the prefix.
      return p.eventDate.startsWith(`${yearFilter}-`);
    });
  }, [searchQuery.data, yearFilter]);

  const people = personsQuery.data?.persons ?? [];
  const isLoading = searchQuery.isLoading || personsQuery.isLoading;
  const isEmpty = !isLoading && results.length === 0 && people.length === 0;

  if (textQuery.length < 2) return null;

  return (
    <div
      style={{
        marginTop: 8,
        border: "1px solid var(--c-border)",
        borderRadius: 8,
        background: "var(--c-bg)",
        maxHeight: 300,
        overflowY: "auto",
      }}
    >
      {isLoading && (
        <p
          className="text-muted"
          style={{ padding: "12px 16px", margin: 0, fontSize: "0.85rem" }}
        >
          Searching…
        </p>
      )}
      {isEmpty && (
        <p
          className="text-muted"
          style={{ padding: "12px 16px", margin: 0, fontSize: "0.85rem" }}
        >
          No results{yearFilter !== null ? ` from ${yearFilter}` : ""}
        </p>
      )}

      {/* People (name search). Year filter doesn't apply to people. */}
      {people.length > 0 && (
        <div
          className="text-subtle"
          style={{
            padding: "8px 16px 4px",
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          People
        </div>
      )}
      {people.map((person) => {
        const name = personDisplayName(person);
        const years = [
          person.birthDate?.slice(0, 4),
          person.deathDate?.slice(0, 4),
        ];
        const yearLabel =
          years[0] || years[1] ? `${years[0] ?? "?"}–${years[1] ?? ""}` : null;
        return (
          <a
            key={person.id}
            href={`/person/${person.id}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 16px",
              borderBottom: "1px solid var(--c-border)",
              textDecoration: "none",
              color: "var(--c-text)",
            }}
          >
            {person.coverUrl ? (
              <img
                src={person.coverUrl}
                alt=""
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "var(--c-bg-alt)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {name.charAt(0).toUpperCase() || "?"}
              </span>
            )}
            <span style={{ minWidth: 0 }}>
              <strong style={{ fontSize: "0.9rem" }}>{name}</strong>
              {yearLabel && (
                <span
                  className="text-subtle"
                  style={{ fontSize: "0.78rem", marginLeft: 6 }}
                >
                  {yearLabel}
                </span>
              )}
            </span>
          </a>
        );
      })}

      {/* Posts */}
      {people.length > 0 && results.length > 0 && (
        <div
          className="text-subtle"
          style={{
            padding: "8px 16px 4px",
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Posts
        </div>
      )}
      {results.map((post) => (
        <a
          key={post.id}
          href={`/post/${post.id}`}
          style={{
            display: "block",
            padding: "10px 16px",
            borderBottom: "1px solid var(--c-border)",
            textDecoration: "none",
            color: "var(--c-text)",
          }}
        >
          <strong style={{ fontSize: "0.9rem" }}>
            {post.title || post.body.slice(0, 60)}
          </strong>
          <div
            className="text-subtle"
            style={{ fontSize: "0.8rem", marginTop: 2 }}
          >
            {post.author.displayName ?? "Unknown"} ·{" "}
            {(() => {
              const d = new Date(post.createdAt);
              const diff = Date.now() - d.getTime();
              const mins = Math.round(diff / 60_000);
              if (mins < 1) return "just now";
              if (mins < 60) return `${mins}m ago`;
              const hours = Math.round(mins / 60);
              if (hours < 24) return `${hours}h ago`;
              const days = Math.round(hours / 24);
              if (days < 7) return `${days}d ago`;
              return new Date(post.createdAt).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
            })()}
          </div>
        </a>
      ))}
    </div>
  );
}

/**
 * Combined search + year filter input. The pictured-year dropdown was
 * folded into this so users can express "find Y from year X" in one go.
 *
 * - Type a year (e.g. 1979) → timeline filters to that year.
 * - Type text → posts/search runs.
 * - Type both → search results filter to that year.
 *
 * A small chip below the input confirms when a year token has been
 * detected, with an ✕ to clear it.
 *
 * On mobile (≤ 480 px) the input is hidden behind a 🔍 icon button to
 * save vertical space. Tapping the icon expands the full input; blur or
 * Escape collapses it again (unless there's text in it).
 */
function CombinedSearchBar({
  familySlug,
  onYearChange,
}: {
  familySlug: string;
  onYearChange: (year: number | null) => void;
}) {
  const [raw, setRaw] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [countryFilter, setCountryFilter] = useState<string | null>(null);
  const [personFilter, setPersonFilter] = useState<{ id: string; name: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { year, text } = parseYearFromQuery(raw);

  // Fetch persons for the people filter dropdown
  const personsQuery = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug],
    queryFn: () => apiFetch<PersonsListResponse>(`/families/${familySlug}/persons`),
    enabled: showFilters,
  });

  // Extract unique countries from persons data
  const countries = useMemo(() => {
    const persons = personsQuery.data?.persons ?? [];
    const set = new Set<string>();
    for (const p of persons) {
      if (p.familyName) set.add(p.familyName);
    }
    return [...set].sort();
  }, [personsQuery.data]);

  // The header magnifier: open the box, scroll it into view, focus it.
  const openFromHeader = () => {
    setExpanded(true);
    setTimeout(() => {
      inputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      inputRef.current?.focus();
    }, 0);
  };
  useHeaderSearch("posts", openFromHeader);

  // Pages without their own search send the magnifier here with ?search=1
  // (they can't reach this component before it mounts). Open the box and
  // drop the param so a reload doesn't re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("search") !== "1") return;
    openFromHeader();
    const next = new URLSearchParams(searchParams);
    next.delete("search");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setRawAndPropagate = (next: string) => {
    setRaw(next);
    const parsed = parseYearFromQuery(next);
    onYearChange(parsed.year);
  };

  function expand() {
    setExpanded(true);
    // Focus after the DOM updates.
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function maybeCollapse() {
    // Keep expanded if there's text — user probably wants to see results.
    if (!raw.trim() && !showFilters) setExpanded(false);
  }

  // Build the effective text query including filters
  const effectiveText = useMemo(() => {
    let q = text;
    if (personFilter) {
      q = q ? `${q} ${personFilter.name}` : personFilter.name;
    }
    if (countryFilter) {
      q = q ? `${q} ${countryFilter}` : countryFilter;
    }
    return q;
  }, [text, personFilter, countryFilter]);

  return (
    <div className={`combined-search ${expanded ? "is-expanded" : ""}`}>
      <div className="combined-search__input-row">
        <input
          ref={inputRef}
          type="text"
          value={raw}
          onChange={(e) => setRawAndPropagate(e.target.value)}
          onFocus={() => setExpanded(true)}
          onBlur={maybeCollapse}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              maybeCollapse();
              inputRef.current?.blur();
            }
          }}
          placeholder="Search posts… (try a year like 1979)"
          style={{
            width: "100%",
            padding: "10px 16px",
            fontSize: "0.9rem",
            border: "1px solid var(--c-border-strong)",
            borderRadius: "var(--r-pill)",
            background: "var(--c-bg)",
            color: "var(--c-text)",
            minHeight: 44,
          }}
        />
      </div>

      {/* Filter toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)", marginTop: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn-tertiary"
          onClick={() => setShowFilters(!showFilters)}
          style={{ fontSize: "0.8rem", padding: "4px 10px", minHeight: 28 }}
        >
          {showFilters ? "Hide filters" : "⚙ Filters"}
        </button>

        {/* Active filter chips */}
        {year !== null && (
          <span className="badge" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.75rem" }}>
            📅 {year}
            <button
              type="button"
              aria-label={`Clear year ${year}`}
              onClick={() => setRawAndPropagate(text)}
              style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: 0, fontSize: "1rem", lineHeight: 1 }}
            >
              ×
            </button>
          </span>
        )}
        {countryFilter && (
          <span className="badge" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.75rem" }}>
            🌍 {countryFilter}
            <button
              type="button"
              aria-label={`Clear country filter`}
              onClick={() => setCountryFilter(null)}
              style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: 0, fontSize: "1rem", lineHeight: 1 }}
            >
              ×
            </button>
          </span>
        )}
        {personFilter && (
          <span className="badge" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.75rem" }}>
            👤 {personFilter.name}
            <button
              type="button"
              aria-label={`Clear person filter`}
              onClick={() => setPersonFilter(null)}
              style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: 0, fontSize: "1rem", lineHeight: 1 }}
            >
              ×
            </button>
          </span>
        )}
      </div>

      {/* Filter dropdowns */}
      {showFilters && (
        <div style={{ display: "flex", gap: "var(--s-2)", marginTop: 8, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 140px", minWidth: 120 }}>
            <label htmlFor="filter-country" style={{ fontSize: "0.75rem", display: "block", marginBottom: 2 }}>Family name</label>
            <select
              id="filter-country"
              value={countryFilter ?? ""}
              onChange={(e) => setCountryFilter(e.target.value || null)}
              style={{ width: "100%", fontSize: "0.85rem", padding: "6px 8px", minHeight: 32 }}
            >
              <option value="">All</option>
              {countries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 180px", minWidth: 140 }}>
            <label htmlFor="filter-person" style={{ fontSize: "0.75rem", display: "block", marginBottom: 2 }}>Person</label>
            <select
              id="filter-person"
              value={personFilter?.id ?? ""}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) { setPersonFilter(null); return; }
                const p = personsQuery.data?.persons.find((x) => x.id === id);
                if (p) setPersonFilter({ id: p.id, name: personDisplayName(p) });
              }}
              style={{ width: "100%", fontSize: "0.85rem", padding: "6px 8px", minHeight: 32 }}
            >
              <option value="">All</option>
              {(personsQuery.data?.persons ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {personDisplayName(p)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <PostSearch familySlug={familySlug} textQuery={effectiveText} yearFilter={year} />
    </div>
  );
}

function WelcomeBanner({
  familySlug,
  userName,
  needsOnboarding,
  coverUrl,
  registeredCount,
}: {
  familySlug: string;
  userName: string;
  needsOnboarding: boolean;
  coverUrl: string | null;
  registeredCount: number;
}) {
  // Computed server-side. This used to be done here over the full persons
  // list, so "in the tree" counted everybody on record — the unlinked, the
  // photo-tagged, the in-laws' own relatives — and said 731 while the Tree
  // audit said 691. One definition now, and it is the audit's.
  const statsQuery = useQuery<TreeStatsResponse>({
    queryKey: ["tree-stats", familySlug],
    queryFn: () =>
      apiFetch<TreeStatsResponse>(`/families/${familySlug}/tree-stats`),
  });
  const stats = statsQuery.data;


  const treeStats = stats
    ? [
        { label: "in the tree", value: stats.inTree, show: stats.inTree > 0 },
        { label: "generations", value: stats.generations, show: stats.generations > 1 },
        { label: "branches", value: stats.branches, show: stats.branches > 1 },
        { label: "years", value: stats.yearSpan, show: stats.yearSpan > 10 },
      ].filter((s) => s.show)
    : [];

  return (
    <div className="welcome-bar">
      {coverUrl && (
        <div
          className="welcome-bar__photo"
          style={{ backgroundImage: `url(${coverUrl})` }}
          aria-hidden="true"
        />
      )}

      {/* Greeting */}
      <div className="welcome-bar__greeting">
        <h1>Welcome home, {userName || "friend"}.</h1>
      </div>

      {/* Stats: registered site accounts first, then the tree figures */}
      {(registeredCount > 0 || treeStats.length > 0) && (
        <div className="welcome-bar__stats">
          {registeredCount > 0 && (
            <div className="welcome-stat welcome-stat--registered">
              <b>{registeredCount}</b>
              <small>registered members</small>
            </div>
          )}
          {treeStats.map((s) => (
            <div key={s.label} className="welcome-stat">
              <b>{s.value}</b>
              <small>{s.label}</small>
            </div>
          ))}
        </div>
      )}

      {/* CTA */}
      {needsOnboarding && (
        <a
          href="/profile"
          style={{
            fontSize: "0.8rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            textDecoration: "none",
            color: "var(--c-text)",
            border: "1px solid var(--c-border-strong)",
            borderRadius: "var(--r-pill)",
            padding: "8px 16px",
            whiteSpace: "nowrap",
          }}
        >
          • Complete your profile →
        </a>
      )}
    </div>
  );
}

const LANDING_CARDS = [
  { label: "World", to: "/world" },
  { label: "Family Members", to: "/members" },
  { label: "Family Tree", to: "/tree" },
  { label: "Events", to: "/events" },
  { label: "Albums", to: "/albums" },
  { label: "Gallery", to: "/gallery" },
];

function HomeLanding({ familyName, coverUrl }: { familyName: string; coverUrl: string | null }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-5)", alignItems: "start" }}>
      <div className="stack" style={{ gap: "var(--s-3)" }}>
        <h1
          style={{
            fontFamily: "var(--font-heading, Georgia, serif)",
            fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
            lineHeight: 1.15,
            margin: 0,
          }}
        >
          Welcome to the{" "}
          <span style={{ color: "var(--c-accent, var(--c-blue))" }}>{familyName.replace(" Family", "")}</span>{" "}
          family website.
        </h1>
        <p className="text-muted" style={{ lineHeight: 1.6, maxWidth: 460 }}>
          A living archive of the {familyName} — a place to meet relatives,
          find photographs, trace the family back through generations, and keep
          our story together.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "var(--s-2)",
            marginTop: "var(--s-2)",
          }}
        >
          {LANDING_CARDS.map((card) => (
            <Link
              key={card.to}
              to={card.to}
              style={{
                display: "block",
                padding: "14px 16px",
                border: "1px solid var(--c-border)",
                borderRadius: 6,
                textDecoration: "none",
                color: "var(--c-text)",
                fontSize: "0.8rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              }}
            >
              {card.label}
              <div style={{ marginTop: 4, fontSize: "0.9rem" }}>→</div>
            </Link>
          ))}
        </div>
      </div>
      {/* Right column — featured family photo */}
      <div
        style={{
          aspectRatio: "4 / 5",
          background: coverUrl
            ? `url(${coverUrl}) center / cover`
            : "var(--c-bg-alt)",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.85rem",
          overflow: "hidden",
        }}
        className="text-muted hide-mobile"
      >
        {!coverUrl && (
          <span style={{ opacity: 0.4 }}>Set a cover photo in Settings</span>
        )}
      </div>
    </div>
  );
}

function HomeLeftSidebar({ familySlug }: { familySlug: string }) {
  const eventsQuery = useQuery<EventsListResponse>({
    queryKey: ["events", familySlug],
    queryFn: () => apiFetch<EventsListResponse>(`/families/${familySlug}/events`),
  });

  // Show upcoming events (date >= today, then undated ones), max 4
  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return (eventsQuery.data?.events ?? [])
      .filter((e) => !e.eventDate || e.eventDate >= today)
      .sort((a, b) =>
        (a.eventDate ?? "9999").localeCompare(b.eventDate ?? "9999"),
      )
      .slice(0, 4);
  }, [eventsQuery.data]);

  // Recent events (past), max 3
  const recent = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return (eventsQuery.data?.events ?? [])
      .flatMap((e) => (e.eventDate && e.eventDate < today ? [{ ...e, eventDate: e.eventDate }] : []))
      .sort((a, b) => b.eventDate.localeCompare(a.eventDate))
      .slice(0, 3);
  }, [eventsQuery.data]);

  return (
    <div className="stack" style={{ gap: "var(--s-4)" }}>
      {/* Upcoming Events */}
      {upcoming.length > 0 && (
        <div className="card" style={{ padding: "var(--s-3)" }}>
          <h3 style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 var(--s-3)", color: "var(--c-text-subtle)" }}>
            Upcoming
          </h3>
          <div className="stack" style={{ gap: "var(--s-2)" }}>
            {upcoming.map((evt) => (
              <Link
                key={evt.id}
                to={`/events/${evt.id}`}
                style={{
                  display: "block",
                  padding: "8px 0",
                  borderBottom: "1px solid var(--c-border)",
                  textDecoration: "none",
                  color: "var(--c-text)",
                }}
              >
                <div style={{ fontSize: "0.8rem", fontWeight: 600 }}>{evt.title}</div>
                <div style={{ fontSize: "0.7rem", color: "var(--c-text-subtle)", marginTop: 2 }}>
                  📅 {evt.eventDate
                    ? new Date(evt.eventDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : "Date TBA"}
                </div>
              </Link>
            ))}
          </div>
          <Link
            to="/events"
            style={{ display: "block", marginTop: "var(--s-3)", fontSize: "0.8rem", color: "var(--c-accent, var(--c-blue))", textDecoration: "none" }}
          >
            All events →
          </Link>
        </div>
      )}

      {/* Recent Events */}
      {recent.length > 0 && (
        <div className="card" style={{ padding: "var(--s-3)" }}>
          <h3 style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 var(--s-3)", color: "var(--c-text-subtle)" }}>
            Recent Events
          </h3>
          <div className="stack" style={{ gap: "var(--s-2)" }}>
            {recent.map((evt) => (
              <Link
                key={evt.id}
                to={`/events/${evt.id}`}
                style={{
                  display: "block",
                  padding: "6px 0",
                  textDecoration: "none",
                  color: "var(--c-text)",
                  fontSize: "0.8rem",
                }}
              >
                <span style={{ fontWeight: 500 }}>{evt.title}</span>
                <span style={{ color: "var(--c-text-subtle)", marginLeft: 6 }}>
                  {new Date(evt.eventDate).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Fallback if no events */}
      {upcoming.length === 0 && recent.length === 0 && (
        <div className="card" style={{ padding: "var(--s-3)" }}>
          <h3 style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 var(--s-2)", color: "var(--c-text-subtle)" }}>
            Events
          </h3>
          <p className="text-muted" style={{ fontSize: "0.8rem", margin: 0 }}>
            No events yet.
          </p>
          <Link
            to="/events"
            style={{ display: "block", marginTop: "var(--s-2)", fontSize: "0.8rem", color: "var(--c-accent, var(--c-blue))", textDecoration: "none" }}
          >
            Create an event →
          </Link>
        </div>
      )}
    </div>
  );
}

function HomeSidebar({ familySlug }: { familySlug: string }) {
  const personsQuery = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug],
    queryFn: () => apiFetch<PersonsListResponse>(`/families/${familySlug}/persons`),
  });

  // Show a selection of members (those with cover photos first, max 8)
  const members = useMemo(() => {
    const all = personsQuery.data?.persons ?? [];
    const withPhoto = all.filter((p) => p.coverUrl);
    const rest = all.filter((p) => !p.coverUrl);
    return [...withPhoto, ...rest].slice(0, 8);
  }, [personsQuery.data]);

  return (
    <div className="stack" style={{ gap: "var(--s-4)" }}>
      {/* Family Members */}
      <div className="card" style={{ padding: "var(--s-3)" }}>
        <h3 style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 var(--s-3)", color: "var(--c-text-subtle)" }}>
          Family Members
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "var(--s-2)" }}>
          {members.map((p) => (
            <Link
              key={p.id}
              to={`/person/${p.id}`}
              title={personDisplayName(p)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                textDecoration: "none",
                color: "var(--c-text)",
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  overflow: "hidden",
                  background: p.coverUrl ? "var(--c-bg-alt)" : "var(--c-accent, var(--c-blue))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                }}
              >
                {p.coverUrl ? (
                  <img src={p.coverUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span>{(p.givenName?.[0] ?? "") + (p.familyName?.[0] ?? "")}</span>
                )}
              </div>
              <span style={{ fontSize: "0.65rem", textAlign: "center", lineHeight: 1.2, maxWidth: 50, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.givenName}
              </span>
            </Link>
          ))}
        </div>
        <Link
          to="/tree"
          style={{ display: "block", marginTop: "var(--s-3)", fontSize: "0.8rem", color: "var(--c-accent, var(--c-blue))", textDecoration: "none" }}
        >
          View family tree →
        </Link>
      </div>

      {/* Quick Links */}
      <div className="card" style={{ padding: "var(--s-3)" }}>
        <h3 style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 var(--s-3)", color: "var(--c-text-subtle)" }}>
          Explore
        </h3>
        <nav className="stack" style={{ gap: "var(--s-2)" }}>
          {[
            { to: "/world", label: "🌍 World Map" },
            { to: "/events", label: "📅 Events" },
            { to: "/albums", label: "📷 Albums" },
            { to: "/gallery", label: "🖼 Gallery" },
            { to: "/news", label: "📰 News" },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              style={{
                display: "block",
                padding: "8px 12px",
                borderRadius: 6,
                textDecoration: "none",
                color: "var(--c-text)",
                fontSize: "0.85rem",
                border: "1px solid var(--c-border)",
              }}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}

const FEED_SORT_KEY = "home.feedSort";

/** The viewer's last feed order. Newest-first unless they picked "By year";
 *  storage can be unavailable (private mode), so fall back quietly. */
function readFeedSort(): TimelineSort {
  try {
    return localStorage.getItem(FEED_SORT_KEY) === "year" ? "year" : "recent";
  } catch {
    return "recent";
  }
}

/**
 * Newest | By year switch above the feed. Newest is the default so each
 * visit opens on what was just added; By year is the historical timeline.
 */
function FeedSortToggle({
  value,
  onChange,
}: {
  value: TimelineSort;
  onChange: (next: TimelineSort) => void;
}) {
  const options: { value: TimelineSort; label: string }[] = [
    { value: "recent", label: "Newest" },
    { value: "year", label: "By year" },
  ];
  return (
    <div className="feed-sort" role="group" aria-label="Order posts">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`feed-sort__option${value === o.value ? " is-active" : ""}`}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function HomePage() {
  const meQuery = useMe();
  const [picturedYear, setPicturedYear] = useState<number | null>(null);
  const [feedSort, setFeedSort] = useState<TimelineSort>(readFeedSort);
  const changeFeedSort = (next: TimelineSort) => {
    setFeedSort(next);
    try {
      localStorage.setItem(FEED_SORT_KEY, next);
    } catch {
      // Not persisted; the choice still applies for this visit.
    }
  };
  // "About this site" in the drawer links to /?welcome=1 so anyone can
  // reopen the editorial welcome after their welcome window has passed.
  const [searchParams] = useSearchParams();
  const welcomeRequested = searchParams.get("welcome") === "1";

  const primary = resolveMembership(meQuery.data);
  // Landing-only families always see the hero (there is nothing else).
  // "Both" shows it during the welcome window, or on request.
  const showLanding =
    primary?.homeLayout === "landing" ||
    (primary?.homeLayout === "both" && (primary.showWelcome || welcomeRequested));

  return (
    <>
      <AppHeader />

      <main className="page stack-lg">
        {meQuery.isLoading && <p className="text-muted">Loading…</p>}
        {meQuery.error && (
          <p className="text-error">
            Error: {(meQuery.error as Error).message}
          </p>
        )}

        {meQuery.data && !primary && (
          <section className="stack">
            <h1>Welcome</h1>
            {meQuery.data.user.email ? (
              <p className="text-muted">
                You're in the queue! An admin reviews every new member — there's
                nothing more you need to do. We'll email you at{" "}
                <strong>{meQuery.data.user.email}</strong> as soon as you're
                approved. If you received an invite link, make sure you've
                opened it while signed in.
              </p>
            ) : (
              <p className="text-muted">
                You're in the queue! An admin reviews every new member. Check
                back here soon, or contact an admin if you've been waiting a
                while. If you received an invite link, make sure you've opened
                it while signed in.
              </p>
            )}
          </section>
        )}

        {primary && (
          <section className="stack-lg">
            {/* Welcome banner with family stats */}
            <WelcomeBanner
              familySlug={primary.familySlug}
              coverUrl={primary.coverUrl}
              registeredCount={primary.registeredCount}
              userName={meQuery.data?.user.firstName ?? meQuery.data?.user.displayName ?? ""}
              needsOnboarding={meQuery.data?.user.needsOnboarding ?? false}
            />

            {/* Home-screen install, then notifications — hidden once done */}
            <InstallAppCard />

            {/* Landing section — editorial welcome with nav cards */}
            {showLanding && (
              <HomeLanding familyName={primary.familyName} coverUrl={primary.coverUrl ?? null} />
            )}

            {/* Divider between landing and timeline when both are shown */}
            {showLanding && primary.homeLayout === "both" && (
              <hr style={{ border: "none", borderTop: "1px solid var(--c-border)", margin: "var(--s-4) 0" }} />
            )}

            {/* Timeline section */}
            {(primary.homeLayout === "timeline" || primary.homeLayout === "both") && (
              <div className="home-two-col">
                <aside className="home-two-col__left-sidebar">
                  <HomeLeftSidebar familySlug={primary.familySlug} />
                </aside>
                <div className="home-two-col__main">
                  {can(primary.role, "post:create") && (
                    <NewPostForm familySlug={primary.familySlug} />
                  )}
                  <CombinedSearchBar
                    familySlug={primary.familySlug}
                    onYearChange={setPicturedYear}
                  />
                  <FeedSortToggle value={feedSort} onChange={changeFeedSort} />
                  <Timeline
                    familySlug={primary.familySlug}
                    picturedYear={picturedYear}
                    sort={feedSort}
                  />
                </div>
                <aside className="home-two-col__sidebar">
                  <HomeSidebar familySlug={primary.familySlug} />
                </aside>
              </div>
            )}
          </section>
        )}
      </main>
    </>
  );
}
